/**
 * Finance Service
 * API calls for the Finance & Accounting module
 * Covers all 80 endpoints: Vouchers, Allocations, Ledgers, Subledgers,
 * Cheques, Reports, Dashboard, Lookups, Search, and Settings.
 */

import { authFetch, authFetchBlob, triggerBlobDownload, ApiError, API_BASE_URL } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { getTenantMoney } from '@/lib/tenant-format'
import type { SelectedPurchaseMoney } from '@/features/finance/types/jobMoney'
import type { EmailThreadResponse } from '@/components/common/EmailThreadDrawer'
import type {
  ApiResponse,
  PagedResponse,
  // Vouchers
  VoucherListItem,
  VoucherDetail,
  VoucherSummary,
  VoucherActionResponse,
  CancelAndRedraftResponse,
  VoucherFilters,
  CreateVoucherRequest,
  UpdateVoucherRequest,
  QuickPaymentRequest,
  QuickReceiptRequest,
  ReceiptWithAllocationsRequest,
  PaymentWithAllocationsRequest,
  VoucherWithAllocationsResponse,
  SetAllowAllocationRequest,
  // Allocations
  Allocation,
  AllocationFilters,
  PendingAllocationItem,
  CreateAllocationRequest,
  AutoAllocateRequest,
  OutstandingInvoicesResponse,
  AllocationsPendingSummary,
  // Summary types
  ChequeSummary,
  AgingSummary,
  // Ledgers
  Ledger,
  LedgerTreeNode,
  LedgerFilters,
  CreateLedgerRequest,
  UpdateLedgerRequest,
  NextLedgerCodeResponse,
  // Ledger Groups
  LedgerGroup,
  CreateLedgerGroupRequest,
  UpdateLedgerGroupRequest,
  // Subledgers
  Subledger,
  SubledgerFilters,
  SubledgerSummary,
  CreateSubledgerRequest,
  UpdateSubledgerRequest,
  ReconciliationResult,
  // Subledger Types
  SubledgerType,
  CreateSubledgerTypeRequest,
  UpdateSubledgerTypeRequest,
  // Cheques
  ChequeFilters,
  ChequeListItem,
  ChequeDetailResponse,
  BounceRequest,
  BounceResponse,
  CancelChequeRequest,
  WorkQueueItem,
  // Dashboard
  DashboardSummary,
  // Reports
  TrialBalanceResponse,
  BalanceSheetResponse,
  ProfitLossResponse,
  GeneralLedgerResponse,
  LedgerPartiesResponse,
  LedgerSummaryResponse,
  StatementResponse,
  StatementOfAccountResponse,
  AgingResponse,
  VatReportResponse,
  CashPositionResponse,
  // Search + Quick Lookup
  SearchResultsResponse,
  QuickLookupResponse,
  // Lookups
  LookupItem,
  PaymentMethodItem,
  // Settings
  VoucherTypeResponse,
  CreateVoucherTypeRequest,
  UpdateVoucherTypeRequest,
  AccountMappingResponse,
  CreateAccountMappingRequest,
  UpdateAccountMappingRequest,
  FiscalPeriodResponse,
  CreateFiscalPeriodRequest,
  // Audit
  AuditTrailEntryResponse,
  // Invoice Layer
  InvoiceListItem,
  InvoiceDetail,
  InvoiceSummary,
  InvoiceFilters,
  InvoiceActionResponse,
  RefreshTermsPreviewResponse,
  RefreshTermsResult,
  InvoiceEmailDraft,
  SendInvoiceEmailRequest,
  MarkInvoicePaidRequest,
  MarkInvoicePaidResponse,
  CreateSalesInvoiceFromJORequest,
  CreatePurchaseInvoiceRequest,
  ReplaceInvoiceJobAllocationsRequest,
  ReplaceInvoiceJobAllocationsResponse,
  InvoiceJobAllocation,
  CreateStandaloneSalesInvoiceRequest,
  UpdateInvoiceRequest,
  CreateCreditNoteRequest,
  UpdateActualWeightRequest,
  UpdateActualWeightResponse,
  JobOrderSearchResult,
  InvoiceDocumentResponse,
  PIExtractionResponse,
  CreateCombinedCustomsInvoiceRequest,
  CustomsDutyCandidate,
  CustomsInvoiceJobCandidatesResponse,
} from '@/features/finance/types'

// ============================================
// Helpers
// ============================================

/**
 * Get access token from auth store
 */
function getToken(): string {
  const token = useAuthStore.getState().tokens?.accessToken
  if (!token) {
    throw new ApiError('Not authenticated', 401)
  }
  return token
}

/**
 * Build query string from a params object.
 * Filters out null, undefined, and empty-string values.
 */
function buildQueryString(params: Record<string, unknown>): string {
  const query = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .flatMap(([k, v]) =>
      // Arrays become repeated params (e.g. typeCode=VENDOR&typeCode=CARRIER),
      // which Spring binds to a List<String>.
      Array.isArray(v)
        ? v
            .filter((item) => item != null && item !== '')
            .map((item) => `${encodeURIComponent(k)}=${encodeURIComponent(String(item))}`)
        : [`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`],
    )
    .join('&')
  return query ? `?${query}` : ''
}

// ============================================
// Voucher CRUD (Block 1 FD1)
// ============================================

/**
 * Fetch paginated list of vouchers with optional filters
 * GET /api/v1/vouchers
 */
export async function fetchVouchers(
  filters: VoucherFilters = {}
): Promise<PagedResponse<VoucherListItem>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<VoucherListItem>>>(
    `/api/v1/vouchers${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch voucher summary counts for pipeline cards
 * GET /api/v1/vouchers/summary
 */
export async function fetchVoucherSummary(
  filters: { search?: string; fromDate?: string; toDate?: string; subledgerId?: string } = {}
): Promise<VoucherSummary> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<VoucherSummary>>(
    `/api/v1/vouchers/summary${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch voucher detail by ID
 * GET /api/v1/vouchers/{id}
 */
export async function fetchVoucherDetail(id: string): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    `/api/v1/vouchers/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new voucher
 * POST /api/v1/vouchers
 */
export async function createVoucher(data: CreateVoucherRequest): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    '/api/v1/vouchers',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an existing voucher (DRAFT only)
 * PUT /api/v1/vouchers/{id}
 */
export async function updateVoucher(
  id: string,
  data: UpdateVoucherRequest
): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    `/api/v1/vouchers/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a voucher (DRAFT only)
 * DELETE /api/v1/vouchers/{id}
 */
export async function deleteVoucher(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/vouchers/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Voucher Actions
// ============================================

/**
 * Post a voucher (DRAFT -> POSTED)
 * POST /api/v1/vouchers/{id}/post
 */
export async function postVoucher(id: string): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/post`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Unpost a voucher (POSTED -> DRAFT)
 * POST /api/v1/vouchers/{id}/unpost
 */
export async function unpostVoucher(
  id: string,
  reason: string
): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/unpost`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Cancel a voucher
 * POST /api/v1/vouchers/{id}/cancel
 */
export async function cancelVoucher(
  id: string,
  reason: string
): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/cancel`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Void (permanently cancel) a POSTED voucher — no reversal entry is created.
 * The voucher transitions to CANCELLED and is excluded from all financial
 * reports. Distinct from unpostVoucher (Reverse), which creates an offsetting
 * reversal pair with audit trail. Admin-only on backend (returns 409 if
 * non-admin).
 *
 * POST /api/v1/vouchers/{id}/void
 */
export async function voidVoucher(
  id: string,
  reason: string
): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/void`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Cancel & Re-draft a POSTED voucher — voids the original (permanent cancel, no
 * reversal entry) and creates a fresh DRAFT clone carrying identical details with
 * a new number. Journal/Payment/Receipt only. Returns both so the caller can open
 * the new draft.
 *
 * POST /api/v1/vouchers/{id}/cancel-and-redraft
 */
export async function cancelAndRedraftVoucher(
  id: string,
  reason: string
): Promise<CancelAndRedraftResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<CancelAndRedraftResponse>>(
    `/api/v1/vouchers/${id}/cancel-and-redraft`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Force-close a POSTED receipt/payment/CN/DN voucher. The voucher stays POSTED
 * and visible in GL reports, but it exits the work-queue, dashboard, and
 * allocation pickers. Raw allocationStatus and allocatedAmount are preserved.
 *
 * Typically used for pre-cutoff migrated vouchers whose target invoices were
 * folded into an Opening Balance JV. Audit-logged. Reversible via
 * unforceCloseVoucher.
 *
 * POST /api/v1/vouchers/{id}/force-close
 */
export async function forceCloseVoucher(
  id: string,
  reason: string
): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/force-close`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Reverse a prior force-close. The voucher returns to its raw allocationStatus
 * (UNALLOCATED / PARTIALLY_ALLOCATED) and reappears in work queues. Audit-logged.
 *
 * POST /api/v1/vouchers/{id}/unforce-close
 */
export async function unforceCloseVoucher(
  id: string
): Promise<VoucherActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherActionResponse>>(
    `/api/v1/vouchers/${id}/unforce-close`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Flip the `allowAllocation` opt-in on a journal voucher. Journal nature only.
 * Backend rejects with 409 if the voucher is not journal/POSTED/etc.; rejects with
 * 409 if turning false while active allocations exist (reverse them first).
 *
 * PATCH /api/v1/vouchers/{id}/allow-allocation
 */
export async function setVoucherAllowAllocation(
  id: string,
  request: SetAllowAllocationRequest
): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    `/api/v1/vouchers/${id}/allow-allocation`,
    token,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  )

  return response.data
}

// ============================================
// Quick Entry
// ============================================

/**
 * Create a quick payment voucher
 * POST /api/v1/payments
 */
export async function createQuickPayment(
  data: QuickPaymentRequest
): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    '/api/v1/payments',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Create a quick receipt voucher
 * POST /api/v1/receipts
 */
export async function createQuickReceipt(
  data: QuickReceiptRequest
): Promise<VoucherDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherDetail>>(
    '/api/v1/receipts',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Create a receipt voucher with inline allocations (single atomic operation)
 * POST /api/v1/receipts/with-allocations
 * Creates receipt, posts it, and allocates against invoices in one call.
 */
export async function createReceiptWithAllocations(
  data: ReceiptWithAllocationsRequest
): Promise<VoucherWithAllocationsResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherWithAllocationsResponse>>(
    '/api/v1/receipts/with-allocations',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Create a payment voucher with inline allocations (single atomic operation)
 * POST /api/v1/payments/with-allocations
 * Creates payment, posts it, and allocates against invoices in one call.
 */
export async function createPaymentWithAllocations(
  data: PaymentWithAllocationsRequest
): Promise<VoucherWithAllocationsResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherWithAllocationsResponse>>(
    '/api/v1/payments/with-allocations',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

// ============================================
// Allocations
// ============================================

/**
 * Fetch paginated list of allocations
 * GET /api/v1/allocations
 */
export async function fetchAllocations(
  filters: AllocationFilters = {}
): Promise<PagedResponse<Allocation>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<Allocation>>>(
    `/api/v1/allocations${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch pending (unallocated / partially allocated) items
 * GET /api/v1/allocations/pending
 */
export async function fetchPendingAllocations(
  filters: AllocationFilters = {}
): Promise<PagedResponse<PendingAllocationItem>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<PendingAllocationItem>>>(
    `/api/v1/allocations/pending${qs}`,
    token
  )

  return response.data
}

/**
 * Create manual allocation(s)
 * POST /api/v1/allocations
 */
export async function createAllocation(
  data: CreateAllocationRequest
): Promise<Allocation[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Allocation[]>>(
    '/api/v1/allocations',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Auto-allocate a payment to outstanding invoices
 * POST /api/v1/allocations/auto
 */
export async function autoAllocate(
  data: AutoAllocateRequest
): Promise<Allocation[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Allocation[]>>(
    '/api/v1/allocations/auto',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Reverse (delete) an allocation
 * DELETE /api/v1/allocations/{id}
 */
export async function reverseAllocation(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/allocations/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

/**
 * Fetch outstanding invoices for a subledger
 * GET /api/v1/allocations/outstanding/{subledgerId}
 */
export async function fetchOutstandingInvoices(
  subledgerId: string
): Promise<OutstandingInvoicesResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<OutstandingInvoicesResponse>>(
    `/api/v1/allocations/outstanding/${subledgerId}`,
    token
  )

  return response.data
}

/**
 * Fetch pending allocations summary for pipeline cards
 * GET /api/v1/allocations/pending/summary
 */
export async function fetchAllocationsPendingSummary(): Promise<AllocationsPendingSummary> {
  const token = getToken()

  const response = await authFetch<ApiResponse<AllocationsPendingSummary>>(
    '/api/v1/allocations/pending/summary',
    token
  )

  return response.data
}

// ============================================
// Lookups (Form Dropdowns)
// ============================================

/**
 * Fetch ledger lookup list
 * GET /api/v1/lookups/ledgers
 */
export async function fetchLedgerLookup(): Promise<LookupItem[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LookupItem[]>>(
    '/api/v1/lookups/ledgers',
    token
  )

  return response.data
}

/**
 * Fetch subledger lookup list, optionally filtered by type
 * GET /api/v1/lookups/subledgers
 */
export async function fetchSubledgerLookup(
  typeId?: string
): Promise<LookupItem[]> {
  const token = getToken()
  const qs = typeId ? buildQueryString({ typeId }) : ''

  const response = await authFetch<ApiResponse<LookupItem[]>>(
    `/api/v1/lookups/subledgers${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch voucher type lookup list
 * GET /api/v1/lookups/voucher-types
 */
export async function fetchVoucherTypeLookup(): Promise<LookupItem[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LookupItem[]>>(
    '/api/v1/lookups/voucher-types',
    token
  )

  return response.data
}

/**
 * Fetch payment method lookup list (enriched with default ledger + detail flags)
 * GET /api/v1/lookups/payment-methods
 *
 * @param depositLedgerId - Optional. When provided, returns only methods valid
 *   for that deposit account (e.g. cash-in-hand accounts only return "Cash").
 */
export async function fetchPaymentMethodLookup(
  depositLedgerId?: string
): Promise<PaymentMethodItem[]> {
  const token = getToken()

  const queryParams: Record<string, unknown> = {}
  if (depositLedgerId) queryParams.depositLedgerId = depositLedgerId

  const qs = buildQueryString(queryParams)

  const response = await authFetch<ApiResponse<PaymentMethodItem[]>>(
    `/api/v1/lookups/payment-methods${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch currency lookup list
 * GET /api/v1/lookups/currencies
 */
export async function fetchCurrencyLookup(): Promise<LookupItem[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LookupItem[]>>(
    '/api/v1/lookups/currencies',
    token
  )

  return response.data
}

// ============================================
// Currency & Exchange Rate management (Finance → Settings → Currencies)
// ============================================

/** A currency row with its current exchange rate (base units per 1 foreign unit). */
export interface CurrencyRate {
  id: string
  code: string
  name: string
  symbol: string
  exchangeRate: number
  isBase: boolean
  decimalPlaces: number
  isActive: boolean
}

/**
 * List all currencies with their current exchange rates.
 * GET /api/v1/currencies
 */
export async function fetchCurrencies(): Promise<CurrencyRate[]> {
  const token = getToken()
  const response = await authFetch<ApiResponse<PagedResponse<CurrencyRate>>>(
    '/api/v1/currencies?page=1&size=200',
    token
  )
  return response.data.content
}

/**
 * Update a currency's exchange rate (base units per 1 foreign unit).
 * PUT /api/v1/currencies/{id}/rate
 */
export async function updateCurrencyRate(id: string, exchangeRate: number): Promise<CurrencyRate> {
  const token = getToken()
  const response = await authFetch<ApiResponse<CurrencyRate>>(
    `/api/v1/currencies/${id}/rate`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exchangeRate }),
    }
  )
  return response.data
}

// ============================================
// Paginated Lookup APIs (for dropdowns with server-side search)
// ============================================

/** Params for paginated subledger lookup */
export interface SubledgerLookupParams {
  search?: string
  /** One type code, or several (e.g. ['VENDOR', 'CARRIER']) sent as repeated params */
  typeCode?: string | string[]
  page?: number
  size?: number
}

/**
 * Fetch paginated subledger lookup with server-side search
 * GET /api/v1/lookups/subledgers
 *
 * When page/size params provided, returns PagedResponse.
 * Without pagination params, returns simple array (backward compatible).
 */
export async function fetchSubledgerLookupPaginated(
  params: SubledgerLookupParams = {}
): Promise<PagedResponse<LookupItem>> {
  const token = getToken()
  const { search, typeCode, page = 1, size = 20 } = params

  const queryParams: Record<string, unknown> = { page, size }
  if (search?.trim()) queryParams.search = search.trim()
  if (typeCode) queryParams.typeCode = typeCode

  const qs = buildQueryString(queryParams)

  const response = await authFetch<ApiResponse<PagedResponse<LookupItem>>>(
    `/api/v1/lookups/subledgers${qs}`,
    token
  )

  return response.data
}

/** Params for paginated ledger lookup */
export interface LedgerLookupParams {
  search?: string
  groupType?: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE'
  hasSubledger?: boolean
  page?: number
  size?: number
}

/**
 * Fetch paginated ledger lookup with server-side search
 * GET /api/v1/lookups/ledgers
 *
 * When page/size params provided, returns PagedResponse.
 * Without pagination params, returns simple array (backward compatible).
 */
export async function fetchLedgerLookupPaginated(
  params: LedgerLookupParams = {}
): Promise<PagedResponse<LookupItem>> {
  const token = getToken()
  const { search, groupType, hasSubledger, page = 1, size = 20 } = params

  const queryParams: Record<string, unknown> = { page, size }
  if (search?.trim()) queryParams.search = search.trim()
  if (groupType) queryParams.groupType = groupType
  if (hasSubledger !== undefined) queryParams.hasSubledger = hasSubledger

  const qs = buildQueryString(queryParams)

  const response = await authFetch<ApiResponse<PagedResponse<LookupItem>>>(
    `/api/v1/lookups/ledgers${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch deposit-eligible accounts (cash + bank only)
 * GET /api/v1/lookups/deposit-accounts
 *
 * Backend may return a flat array or a paginated response.
 * Normalizes to PagedResponse for consistent consumption by LedgerDropdown.
 */
export async function fetchDepositAccountsPaginated(
  params: { search?: string; page?: number; size?: number } = {}
): Promise<PagedResponse<LookupItem>> {
  const token = getToken()
  const { search } = params

  const queryParams: Record<string, unknown> = {}
  if (search?.trim()) queryParams.search = search.trim()

  const qs = buildQueryString(queryParams)

  const response = await authFetch<ApiResponse<LookupItem[] | PagedResponse<LookupItem>>>(
    `/api/v1/lookups/deposit-accounts${qs}`,
    token
  )

  const data = response.data

  // If backend returns a flat array, wrap it in PagedResponse shape
  if (Array.isArray(data)) {
    return {
      content: data,
      page: 1,
      size: data.length,
      totalElements: data.length,
      totalPages: 1,
      first: true,
      last: true,
      hasNext: false,
      hasPrevious: false,
    }
  }

  return data
}

/** Params for paginated outstanding invoices */
export interface OutstandingInvoicesParams {
  search?: string
  page?: number
  size?: number
  sortBy?: 'daysOverdue' | 'balance' | 'voucherDate' | 'dueDate'
  sortDir?: 'ASC' | 'DESC'
}

/** Summary for outstanding invoices (totals across all pages) */
export interface OutstandingInvoicesSummary {
  totalOutstanding: number
  totalOverdue: number
  invoiceCount: number
}

/** Paginated response for outstanding invoices */
export interface OutstandingInvoicesPaginatedResponse {
  subledger: { id: string; code: string; name: string }
  content: OutstandingInvoiceItem[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
  summary: OutstandingInvoicesSummary
}

/**
 * Fetch paginated outstanding invoices with server-side search
 * GET /api/v1/allocations/outstanding/{subledgerId}
 */
export async function fetchOutstandingInvoicesPaginated(
  subledgerId: string,
  params: OutstandingInvoicesParams = {}
): Promise<OutstandingInvoicesPaginatedResponse> {
  const token = getToken()
  const { search, page = 1, size = 20, sortBy = 'daysOverdue', sortDir = 'DESC' } = params

  const queryParams: Record<string, unknown> = { page, size, sortBy, sortDir }
  if (search?.trim()) queryParams.search = search.trim()

  const qs = buildQueryString(queryParams)

  const response = await authFetch<ApiResponse<OutstandingInvoicesPaginatedResponse>>(
    `/api/v1/allocations/outstanding/${subledgerId}${qs}`,
    token
  )

  return response.data
}

// ============================================
// Ledgers (Masters - Block 1 FD2)
// ============================================

/**
 * Fetch paginated list of ledgers
 * GET /api/v1/ledgers
 */
export async function fetchLedgers(
  filters: LedgerFilters = {}
): Promise<PagedResponse<Ledger>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<Ledger>>>(
    `/api/v1/ledgers${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch the full ledger tree (hierarchical)
 * GET /api/v1/ledgers/tree
 */
export async function fetchLedgerTree(): Promise<LedgerTreeNode[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LedgerTreeNode[]>>(
    '/api/v1/ledgers/tree',
    token
  )

  return response.data
}

/**
 * Suggest the next available account code for the create-ledger form.
 * Pass parentId for a child account, or groupId for a top-level account.
 * GET /api/v1/ledgers/next-code
 */
export async function fetchNextLedgerCode(params: {
  groupId?: string | null
  parentId?: string | null
}): Promise<string> {
  const token = getToken()

  const query: Record<string, unknown> = {}
  if (params.parentId) query.parentId = params.parentId
  if (params.groupId) query.groupId = params.groupId
  const qs = buildQueryString(query)

  const response = await authFetch<ApiResponse<NextLedgerCodeResponse>>(
    `/api/v1/ledgers/next-code${qs}`,
    token
  )

  return response.data.code
}

/**
 * Fetch ledger detail by ID
 * GET /api/v1/ledgers/{id}
 */
export async function fetchLedgerDetail(id: string): Promise<Ledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Ledger>>(
    `/api/v1/ledgers/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new ledger
 * POST /api/v1/ledgers
 */
export async function createLedger(data: CreateLedgerRequest): Promise<Ledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Ledger>>(
    '/api/v1/ledgers',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an existing ledger
 * PUT /api/v1/ledgers/{id}
 */
export async function updateLedger(
  id: string,
  data: UpdateLedgerRequest
): Promise<Ledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Ledger>>(
    `/api/v1/ledgers/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a ledger (non-system, no transactions)
 * DELETE /api/v1/ledgers/{id}
 */
export async function deleteLedger(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/ledgers/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Ledger Groups
// ============================================

/**
 * Fetch all ledger groups
 * GET /api/v1/ledger-groups
 */
export async function fetchLedgerGroups(): Promise<LedgerGroup[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LedgerGroup[]>>(
    '/api/v1/ledger-groups',
    token
  )

  return response.data
}

/**
 * Fetch ledger group detail by ID
 * GET /api/v1/ledger-groups/{id}
 */
export async function fetchLedgerGroupDetail(id: string): Promise<LedgerGroup> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LedgerGroup>>(
    `/api/v1/ledger-groups/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new ledger group
 * POST /api/v1/ledger-groups
 */
export async function createLedgerGroup(
  data: CreateLedgerGroupRequest
): Promise<LedgerGroup> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LedgerGroup>>(
    '/api/v1/ledger-groups',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an existing ledger group
 * PUT /api/v1/ledger-groups/{id}
 */
export async function updateLedgerGroup(
  id: string,
  data: UpdateLedgerGroupRequest
): Promise<LedgerGroup> {
  const token = getToken()

  const response = await authFetch<ApiResponse<LedgerGroup>>(
    `/api/v1/ledger-groups/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a ledger group (non-system, no child ledgers)
 * DELETE /api/v1/ledger-groups/{id}
 */
export async function deleteLedgerGroup(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/ledger-groups/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Subledgers
// ============================================

/**
 * Fetch paginated list of subledgers
 * GET /api/v1/subledgers
 */
export async function fetchSubledgers(
  filters: SubledgerFilters = {}
): Promise<PagedResponse<Subledger>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<Subledger>>>(
    `/api/v1/subledgers${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch subledger summary (counts by type)
 * GET /api/v1/subledgers/summary
 */
export async function fetchSubledgerSummary(): Promise<SubledgerSummary> {
  const token = getToken()

  const response = await authFetch<ApiResponse<SubledgerSummary>>(
    '/api/v1/subledgers/summary',
    token
  )

  return response.data
}

/**
 * Fetch subledger detail by ID
 * GET /api/v1/subledgers/{id}
 */
export async function fetchSubledgerDetail(id: string): Promise<Subledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Subledger>>(
    `/api/v1/subledgers/${id}`,
    token
  )

  return response.data
}

/**
 * Fetch subledger by external ID (e.g., customerId)
 * GET /api/v1/subledgers/by-external-id/{externalId}
 */
export async function fetchSubledgerByExternalId(externalId: string): Promise<Subledger | null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Subledger | null>>(
    `/api/v1/subledgers/by-external-id/${encodeURIComponent(externalId)}`,
    token
  )

  return response.data
}

/**
 * Create a new subledger
 * POST /api/v1/subledgers
 */
export async function createSubledger(
  data: CreateSubledgerRequest
): Promise<Subledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Subledger>>(
    '/api/v1/subledgers',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an existing subledger
 * PUT /api/v1/subledgers/{id}
 */
export async function updateSubledger(
  id: string,
  data: UpdateSubledgerRequest
): Promise<Subledger> {
  const token = getToken()

  const response = await authFetch<ApiResponse<Subledger>>(
    `/api/v1/subledgers/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a subledger (no transactions)
 * DELETE /api/v1/subledgers/{id}
 */
export async function deleteSubledger(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/subledgers/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

/**
 * Reconcile subledgers with Operations data.
 * Dry run detects missing subledgers; execute creates them.
 * POST /api/v1/subledgers/reconcile
 */
export async function reconcileSubledgers(
  dryRun: boolean = true
): Promise<ReconciliationResult> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ReconciliationResult>>(
    `/api/v1/subledgers/reconcile?dryRun=${dryRun}`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Sync ONE party from Operations into Finance, by its master-data id.
 * POST /api/v1/subledgers/reconcile/{externalId}
 *
 * The counterpart to the full sweep, for "I just created this vendor and need its
 * finance account" — without scanning every party the tenant has.
 *
 * Returns the same shape as the sweep with a single entry, plus the server's
 * human-readable message ("Finance account created" / "Already in sync" / …).
 */
export async function syncSingleSubledger(
  externalId: string,
  dryRun: boolean = false
): Promise<{ result: ReconciliationResult; message: string }> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ReconciliationResult>>(
    `/api/v1/subledgers/reconcile/${encodeURIComponent(externalId)}?dryRun=${dryRun}`,
    token,
    { method: 'POST' }
  )

  return { result: response.data, message: response.message ?? '' }
}

// ============================================
// Subledger Types
// ============================================

/**
 * Fetch all subledger types
 * GET /api/v1/subledger-types
 */
export async function fetchSubledgerTypes(): Promise<SubledgerType[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<SubledgerType[]>>(
    '/api/v1/subledger-types',
    token
  )

  return response.data
}

/**
 * Fetch subledger type detail by ID
 * GET /api/v1/subledger-types/{id}
 */
export async function fetchSubledgerTypeDetail(id: string): Promise<SubledgerType> {
  const token = getToken()

  const response = await authFetch<ApiResponse<SubledgerType>>(
    `/api/v1/subledger-types/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new subledger type
 * POST /api/v1/subledger-types
 */
export async function createSubledgerType(
  data: CreateSubledgerTypeRequest
): Promise<SubledgerType> {
  const token = getToken()

  const response = await authFetch<ApiResponse<SubledgerType>>(
    '/api/v1/subledger-types',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an existing subledger type
 * PUT /api/v1/subledger-types/{id}
 */
export async function updateSubledgerType(
  id: string,
  data: UpdateSubledgerTypeRequest
): Promise<SubledgerType> {
  const token = getToken()

  const response = await authFetch<ApiResponse<SubledgerType>>(
    `/api/v1/subledger-types/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a subledger type (no linked subledgers)
 * DELETE /api/v1/subledger-types/{id}
 */
export async function deleteSubledgerType(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/subledger-types/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Receivables & Payables (Block 2 FD1)
// ============================================

/** Filter params for receivables / payables list */
interface ReceivablesPayablesFilters {
  subledgerId?: string
  status?: string
  overdueDays?: number
  bucket?: string // CURRENT, OVERDUE_1_30, OVERDUE_31_60, OVERDUE_61_90, OVERDUE_90_PLUS
  search?: string
  salesPersonId?: string
  /** Customer payment-terms label, exact match (case-insensitive) against Customer.financials.paymentTerms. */
  paymentTermsLabel?: string
  /** Invoice purpose — FREIGHT / CUSTOMS_DUTY / ADDITIONAL / OTHER (SI only). */
  purpose?: 'FREIGHT' | 'CUSTOMS_DUTY' | 'ADDITIONAL' | 'OTHER'
  page?: number
  size?: number
}

/**
 * Fetch receivables list
 * GET /api/v1/receivables
 */
export async function fetchReceivables(
  filters: ReceivablesPayablesFilters = {}
): Promise<PagedResponse<WorkQueueItem>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<WorkQueueItem>>>(
    `/api/v1/receivables${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch receivables summary for pipeline cards
 * GET /api/v1/receivables/summary
 */
export async function fetchReceivablesSummary(
  filters: {
    salesPersonId?: string
    paymentTermsLabel?: string
    search?: string
    purpose?: 'FREIGHT' | 'CUSTOMS_DUTY' | 'ADDITIONAL' | 'OTHER'
  } = {}
): Promise<AgingSummary> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<AgingSummary>>(
    `/api/v1/receivables/summary${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch payables list
 * GET /api/v1/payables
 */
export async function fetchPayables(
  filters: ReceivablesPayablesFilters = {}
): Promise<PagedResponse<WorkQueueItem>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<WorkQueueItem>>>(
    `/api/v1/payables${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch payables summary for pipeline cards
 * GET /api/v1/payables/summary
 */
export async function fetchPayablesSummary(): Promise<AgingSummary> {
  const token = getToken()

  const response = await authFetch<ApiResponse<AgingSummary>>(
    '/api/v1/payables/summary',
    token
  )

  return response.data
}

// ============================================
// Cheques (Block 2 FD1)
// ============================================

/**
 * Fetch paginated list of cheques
 * GET /api/v1/cheques
 */
export async function fetchCheques(
  filters: ChequeFilters = {}
): Promise<PagedResponse<ChequeListItem>> {
  const token = getToken()

  // Map filter field names to match API query params
  const apiParams: Record<string, unknown> = {
    page: filters.page,
    size: filters.size,
    search: filters.search,
    status: filters.status,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
  }
  // Map `direction` → `chequeType` (API uses chequeType param)
  if (filters.direction) {
    apiParams.chequeType = filters.direction.toLowerCase()
  }

  const qs = buildQueryString(apiParams)

  const response = await authFetch<ApiResponse<PagedResponse<ChequeListItem>>>(
    `/api/v1/cheques${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch cheque summary for pipeline cards
 * GET /api/v1/cheques/summary
 */
export async function fetchChequeSummary(
  filters: { fromDate?: string; toDate?: string } = {}
): Promise<ChequeSummary> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<ChequeSummary>>(
    `/api/v1/cheques/summary${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch cheque detail by ID
 * GET /api/v1/cheques/{id}
 */
export async function fetchChequeDetail(id: string): Promise<ChequeDetailResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ChequeDetailResponse>>(
    `/api/v1/cheques/${id}`,
    token
  )

  return response.data
}

/**
 * Deposit a cheque (PENDING -> DEPOSITED)
 * POST /api/v1/cheques/{id}/deposit
 */
export async function depositCheque(id: string): Promise<ChequeDetailResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ChequeDetailResponse>>(
    `/api/v1/cheques/${id}/deposit`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Clear a cheque (DEPOSITED -> CLEARED)
 * POST /api/v1/cheques/{id}/clear
 */
export async function clearCheque(id: string): Promise<ChequeDetailResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ChequeDetailResponse>>(
    `/api/v1/cheques/${id}/clear`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Bounce a cheque (DEPOSITED -> BOUNCED)
 * POST /api/v1/cheques/{id}/bounce
 */
export async function bounceCheque(id: string, payload: BounceRequest): Promise<BounceResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<BounceResponse>>(
    `/api/v1/cheques/${id}/bounce`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  )

  return response.data
}

/**
 * Cancel a cheque
 * POST /api/v1/cheques/{id}/cancel
 */
export async function cancelCheque(id: string, payload: CancelChequeRequest): Promise<ChequeDetailResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<ChequeDetailResponse>>(
    `/api/v1/cheques/${id}/cancel`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  )

  return response.data
}

// ============================================
// Dashboard (Block 2 FD2)
// ============================================

/**
 * Fetch dashboard summary (KPIs, attention items, recent transactions)
 * GET /api/v1/dashboard/summary
 *
 * The backend caches this for ~60s (it scans all POSTED vouchers to compute
 * ledger balances). Pass `forceRefresh` for the manual refresh button to
 * bypass the cache and recompute immediately (?refresh=true).
 */
export async function fetchDashboardSummary(
  forceRefresh = false
): Promise<DashboardSummary> {
  const token = getToken()

  const response = await authFetch<ApiResponse<DashboardSummary>>(
    forceRefresh ? '/api/v1/dashboard/summary?refresh=true' : '/api/v1/dashboard/summary',
    token
  )

  return response.data
}

// ============================================
// Reports (Block 3 FD2)
// ============================================

/** Common date-range params used by most reports */
interface ReportDateParams {
  fromDate?: string
  toDate?: string
  asOfDate?: string
  comparePrevious?: boolean
  balanceOnly?: boolean
}

/**
 * Fetch Trial Balance report
 * GET /api/v1/reports/trial-balance
 */
export async function fetchTrialBalance(
  params: ReportDateParams = {}
): Promise<TrialBalanceResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<TrialBalanceResponse>>(
    `/api/v1/reports/trial-balance${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch Balance Sheet report
 * GET /api/v1/reports/balance-sheet
 */
export async function fetchBalanceSheet(
  params: ReportDateParams = {}
): Promise<BalanceSheetResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<BalanceSheetResponse>>(
    `/api/v1/reports/balance-sheet${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch Profit & Loss report
 * GET /api/v1/reports/profit-loss
 */
export async function fetchProfitLoss(
  params: ReportDateParams = {}
): Promise<ProfitLossResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<ProfitLossResponse>>(
    `/api/v1/reports/profit-loss${qs}`,
    token
  )

  return response.data
}

/** Params for general ledger report */
interface GeneralLedgerParams {
  ledgerId: string
  fromDate?: string
  toDate?: string
  /** Narrow the ledger to a single subledger (only valid for control accounts). */
  subledgerId?: string
}

/**
 * Fetch General Ledger report
 * GET /api/v1/reports/general-ledger
 */
export async function fetchGeneralLedger(
  params: GeneralLedgerParams
): Promise<GeneralLedgerResponse> {
  const token = getToken()
  const qs = buildQueryString({ ...params })

  const response = await authFetch<ApiResponse<GeneralLedgerResponse>>(
    `/api/v1/reports/general-ledger${qs}`,
    token
  )

  return response.data
}

/**
 * One account's balance split by party for the period (Balance Sheet → Accounts Payable → by party)
 * GET /api/v1/reports/general-ledger/parties
 */
export async function fetchLedgerParties(params: {
  ledgerId: string
  fromDate: string
  toDate: string
}): Promise<LedgerPartiesResponse> {
  const token = getToken()
  const qs = buildQueryString({ ...params })

  const response = await authFetch<ApiResponse<LedgerPartiesResponse>>(
    `/api/v1/reports/general-ledger/parties${qs}`,
    token
  )

  return response.data
}

/** Params for ledger summary report */
interface LedgerSummaryParams {
  groupId?: string
  fromDate?: string
  toDate?: string
}

/**
 * Fetch Ledger Summary report
 * GET /api/v1/reports/ledger-summary
 */
export async function fetchLedgerSummary(
  params: LedgerSummaryParams = {}
): Promise<LedgerSummaryResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<LedgerSummaryResponse>>(
    `/api/v1/reports/ledger-summary${qs}`,
    token
  )

  return response.data
}

/** Params for statement reports */
interface StatementParams {
  fromDate?: string
  toDate?: string
}

/**
 * Fetch Customer Statement
 * GET /api/v1/reports/customer-statement/{subledgerId}
 */
export async function fetchCustomerStatement(
  subledgerId: string,
  params: StatementParams = {}
): Promise<StatementResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<StatementResponse>>(
    `/api/v1/reports/customer-statement/${subledgerId}${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch Vendor Statement
 * GET /api/v1/reports/vendor-statement/{subledgerId}
 */
export async function fetchVendorStatement(
  subledgerId: string,
  params: StatementParams = {}
): Promise<StatementResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<StatementResponse>>(
    `/api/v1/reports/vendor-statement/${subledgerId}${qs}`,
    token
  )

  return response.data
}

/** Params for aging report */
interface AgingParams {
  asOfDate?: string
  type?: 'RECEIVABLE' | 'PAYABLE'
  bucket?: 'current' | '1-30' | '31-60' | '61-90' | 'over90'
  accountOwnerId?: string
  /** Invoice purpose — FREIGHT / CUSTOMS_DUTY / ADDITIONAL / OTHER (SI only). */
  purpose?: 'FREIGHT' | 'CUSTOMS_DUTY' | 'ADDITIONAL' | 'OTHER'
  /** Customer payment-terms label, exact match against Customer.financials.paymentTerms. */
  paymentTermsLabel?: string
}

/**
 * Fetch Aging report
 * GET /api/v1/reports/aging
 */
export async function fetchAgingReport(
  params: AgingParams = {}
): Promise<AgingResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<AgingResponse>>(
    `/api/v1/reports/aging${qs}`,
    token
  )

  return response.data
}

/** Params for VAT report */
interface VATParams {
  fromDate?: string
  toDate?: string
}

/**
 * Fetch VAT report
 * GET /api/v1/reports/vat
 */
export async function fetchVATReport(
  params: VATParams = {}
): Promise<VatReportResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<VatReportResponse>>(
    `/api/v1/reports/vat${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch Cash Position report
 * GET /api/v1/reports/cash-position
 */
export async function fetchCashPosition(): Promise<CashPositionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<CashPositionResponse>>(
    '/api/v1/reports/cash-position',
    token
  )

  return response.data
}

// ============================================
// Search (Block 4 FD1)
// ============================================

/**
 * Global finance search across vouchers, ledgers, subledgers
 * GET /api/v1/search
 */
export async function financeSearch(
  query: string,
  categories?: string,
  limit?: number
): Promise<SearchResultsResponse> {
  const token = getToken()
  const qs = buildQueryString({ q: query, categories, limit })

  const response = await authFetch<ApiResponse<SearchResultsResponse>>(
    `/api/v1/search${qs}`,
    token
  )

  return response.data
}

/**
 * Quick lookup for a subledger (balance, recent transactions)
 * GET /api/v1/quick-lookup/{subledgerId}
 */
export async function fetchQuickLookup(subledgerId: string): Promise<QuickLookupResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<QuickLookupResponse>>(
    `/api/v1/quick-lookup/${subledgerId}`,
    token
  )

  return response.data
}

// ============================================
// Settings — Voucher Type Configs (Block 4 FD2)
// ============================================

/**
 * Fetch all voucher type configurations (paginated)
 * GET /api/v1/voucher-types
 */
export async function fetchVoucherTypeConfigs(): Promise<PagedResponse<VoucherTypeResponse>> {
  const token = getToken()

  const response = await authFetch<ApiResponse<PagedResponse<VoucherTypeResponse>>>(
    '/api/v1/voucher-types?page=1&size=50',
    token
  )

  return response.data
}

/**
 * Fetch voucher type config detail by ID
 * GET /api/v1/voucher-types/{id}
 */
export async function fetchVoucherTypeConfigDetail(
  id: string
): Promise<VoucherTypeResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherTypeResponse>>(
    `/api/v1/voucher-types/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new voucher type configuration
 * POST /api/v1/voucher-types
 */
export async function createVoucherTypeConfig(
  data: CreateVoucherTypeRequest
): Promise<VoucherTypeResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherTypeResponse>>(
    '/api/v1/voucher-types',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update a voucher type configuration
 * PUT /api/v1/voucher-types/{id}
 */
export async function updateVoucherTypeConfig(
  id: string,
  data: UpdateVoucherTypeRequest
): Promise<VoucherTypeResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<VoucherTypeResponse>>(
    `/api/v1/voucher-types/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete a voucher type configuration (non-system, unused)
 * DELETE /api/v1/voucher-types/{id}
 */
export async function deleteVoucherTypeConfig(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/voucher-types/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Settings — Account Mappings (Block 4 FD2)
// ============================================

/**
 * Fetch all account mappings (paginated)
 * GET /api/v1/account-mappings
 */
export async function fetchAccountMappings(): Promise<PagedResponse<AccountMappingResponse>> {
  const token = getToken()

  const response = await authFetch<ApiResponse<PagedResponse<AccountMappingResponse>>>(
    '/api/v1/account-mappings?page=1&size=50',
    token
  )

  return response.data
}

/**
 * Fetch account mapping detail by ID
 * GET /api/v1/account-mappings/{id}
 */
export async function fetchAccountMappingDetail(
  id: string
): Promise<AccountMappingResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<AccountMappingResponse>>(
    `/api/v1/account-mappings/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new account mapping
 * POST /api/v1/account-mappings
 */
export async function createAccountMapping(
  data: CreateAccountMappingRequest
): Promise<AccountMappingResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<AccountMappingResponse>>(
    '/api/v1/account-mappings',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Update an account mapping
 * PUT /api/v1/account-mappings/{id}
 */
export async function updateAccountMapping(
  id: string,
  data: UpdateAccountMappingRequest
): Promise<AccountMappingResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<AccountMappingResponse>>(
    `/api/v1/account-mappings/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete an account mapping
 * DELETE /api/v1/account-mappings/{id}
 */
export async function deleteAccountMapping(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/account-mappings/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Settings — Fiscal Periods (Block 4 FD2)
// ============================================

/** Params for fetching fiscal periods */
interface FiscalPeriodParams {
  fiscalYear?: number
  status?: string
  page?: number
  size?: number
}

/**
 * Fetch fiscal periods (flat paginated list)
 * GET /api/v1/admin/periods
 */
export async function fetchFiscalPeriods(
  params: FiscalPeriodParams = {}
): Promise<PagedResponse<FiscalPeriodResponse>> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<FiscalPeriodResponse>>>(
    `/api/v1/admin/periods${qs}`,
    token
  )

  return response.data
}

/**
 * Fetch fiscal period detail by ID
 * GET /api/v1/admin/periods/{id}
 */
export async function fetchFiscalPeriodDetail(id: string): Promise<FiscalPeriodResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<FiscalPeriodResponse>>(
    `/api/v1/admin/periods/${id}`,
    token
  )

  return response.data
}

/**
 * Create a new fiscal period
 * POST /api/v1/admin/periods
 */
export async function createFiscalPeriod(
  data: CreateFiscalPeriodRequest
): Promise<FiscalPeriodResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<FiscalPeriodResponse>>(
    '/api/v1/admin/periods',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Close a fiscal period
 * POST /api/v1/admin/periods/{id}/close
 * @param id - Period ID
 * @param reason - Optional reason for closing (for audit trail)
 */
export async function closeFiscalPeriod(id: string, reason?: string): Promise<FiscalPeriodResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<FiscalPeriodResponse>>(
    `/api/v1/admin/periods/${id}/close`,
    token,
    {
      method: 'POST',
      body: reason ? JSON.stringify({ reason }) : undefined,
    }
  )

  return response.data
}

/**
 * Lock a fiscal period (prevents any changes)
 * POST /api/v1/admin/periods/{id}/lock
 */
export async function lockFiscalPeriod(id: string): Promise<FiscalPeriodResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<FiscalPeriodResponse>>(
    `/api/v1/admin/periods/${id}/lock`,
    token,
    { method: 'POST' }
  )

  return response.data
}

/**
 * Reopen a closed fiscal period
 * POST /api/v1/admin/periods/{id}/reopen
 * @param id - Period ID
 * @param reason - Required reason for reopening (for audit trail)
 */
export async function reopenFiscalPeriod(id: string, reason: string): Promise<FiscalPeriodResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<FiscalPeriodResponse>>(
    `/api/v1/admin/periods/${id}/reopen`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

// ============================================
// Settings — Audit Trail (Block 4 FD2)
// ============================================

/** Params for fetching audit trail */
interface AuditTrailParams {
  collection?: string
  documentId?: string
  performedBy?: string
  action?: string
  fromDate?: string
  toDate?: string
  page?: number
  size?: number
}

/**
 * Fetch audit trail entries
 * GET /api/v1/admin/audit-trail
 */
export async function fetchAuditTrail(
  params: AuditTrailParams = {}
): Promise<PagedResponse<AuditTrailEntryResponse>> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<AuditTrailEntryResponse>>>(
    `/api/v1/admin/audit-trail${qs}`,
    token
  )

  return response.data
}

// ============================================
// Export / Download Utilities
// ============================================

export type ExportFormat = 'pdf' | 'excel'

/** File extensions for each export format */
const EXPORT_EXTENSIONS: Record<ExportFormat, string> = {
  pdf: '.pdf',
  excel: '.xlsx',
}

/**
 * Download a file export from the API
 * Uses authFetchBlob for centralized blob fetching with proper base URL handling.
 *
 * API uses path-based format selection: /endpoint/{pdf|excel}
 *
 * @param baseEndpoint - API endpoint without format (e.g., '/api/v1/vouchers')
 * @param params - Query parameters to send with the request
 * @param format - Export format: 'pdf' or 'excel'
 * @param filename - Base filename without extension (extension added automatically)
 */
export async function downloadExport(
  baseEndpoint: string,
  params: Record<string, string | number | boolean | undefined>,
  format: ExportFormat,
  filename: string
): Promise<void> {
  const token = getToken()

  // Build query string (without format - format is in path)
  const qs = buildQueryString(params as Record<string, unknown>)

  // Format is part of the path, not a query param
  const endpoint = `${baseEndpoint}/${format}${qs}`

  const blob = await authFetchBlob(endpoint, token)
  triggerBlobDownload(blob, `${filename}${EXPORT_EXTENSIONS[format]}`)
}

/**
 * Export transactions/vouchers list
 * GET /api/v1/vouchers/{pdf|excel}
 */
export async function exportVouchers(
  params: VoucherFilters,
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/vouchers',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `vouchers-list_${dateStr}`
  )
}

/**
 * Export allocations list
 * GET /api/v1/allocations/{pdf|excel}
 */
export async function exportAllocations(
  params: {
    subledgerId?: string
    paymentVoucherId?: string
    invoiceVoucherId?: string
    includeReversed?: boolean
  },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/allocations',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `allocations-list_${dateStr}`
  )
}

/**
 * Export cheques list
 * GET /api/v1/cheques/{pdf|excel}
 *
 * Note: API uses 'chequeType' param (not 'direction')
 */
export async function exportCheques(
  params: {
    chequeType?: 'issued' | 'received'
    status?: string
    fromDate?: string
    toDate?: string
    subledgerId?: string
  },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/cheques',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `cheque-register_${dateStr}`
  )
}

/**
 * Export receivables work queue
 * GET /api/v1/receivables/{pdf|excel}
 */
export async function exportReceivables(
  params: { asOfDate?: string },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/receivables',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `receivables-work-queue_${dateStr}`
  )
}

/**
 * Export payables work queue
 * GET /api/v1/payables/{pdf|excel}
 */
export async function exportPayables(
  params: { asOfDate?: string },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/payables',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `payables-work-queue_${dateStr}`
  )
}

/**
 * Export chart of accounts (ledgers)
 * GET /api/v1/ledgers/{pdf|excel}
 */
export async function exportLedgers(
  params: { groupId?: string; isActive?: boolean },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/ledgers',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `chart-of-accounts_${dateStr}`
  )
}

/**
 * Export subledger accounts
 * GET /api/v1/subledgers/{pdf|excel}
 */
export async function exportSubledgers(
  params: { typeCode?: string },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/subledgers',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `subledger-accounts_${dateStr}`
  )
}

/**
 * Export account mappings
 * GET /api/v1/account-mappings/{pdf|excel}
 */
export async function exportAccountMappings(format: ExportFormat): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/account-mappings',
    {},
    format,
    `account-mapping-register_${dateStr}`
  )
}

/**
 * Export audit trail
 * GET /api/v1/admin/audit-trail/{pdf|excel}
 * Note: Requires fromDate and toDate query parameters
 */
export async function exportAuditTrail(
  params: {
    fromDate?: string
    toDate?: string
    collection?: string
    action?: string
  },
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  // Ensure fromDate and toDate are always provided (required by backend)
  const fromDate = params.fromDate || dateStr
  const toDate = params.toDate || dateStr
  return downloadExport(
    '/api/v1/admin/audit-trail',
    { ...params, fromDate, toDate } as Record<string, string | number | boolean | undefined>,
    format,
    `audit-trail_${fromDate}_to_${toDate}`
  )
}

/**
 * Export Balance Sheet report
 * GET /api/v1/reports/balance-sheet/{pdf|excel}
 */
export async function exportBalanceSheet(
  params: { asOfDate: string },
  format: ExportFormat
): Promise<void> {
  return downloadExport(
    '/api/v1/reports/balance-sheet',
    params,
    format,
    `balance-sheet_${params.asOfDate}`
  )
}

/**
 * Export Profit & Loss report
 * GET /api/v1/reports/profit-loss/{pdf|excel}
 */
export async function exportProfitLoss(
  params: { fromDate: string; toDate: string },
  format: ExportFormat
): Promise<void> {
  return downloadExport(
    '/api/v1/reports/profit-loss',
    params,
    format,
    `profit-loss_${params.fromDate}_to_${params.toDate}`
  )
}

/**
 * Export Trial Balance report
 * GET /api/v1/reports/trial-balance/{pdf|excel}
 *
 * Note: API uses fromDate/toDate (period-based), not asOfDate
 */
export async function exportTrialBalance(
  params: { fromDate: string; toDate: string; balanceOnly?: boolean },
  format: ExportFormat
): Promise<void> {
  return downloadExport(
    '/api/v1/reports/trial-balance',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `trial-balance_${params.fromDate}_to_${params.toDate}`
  )
}

/**
 * Export General Ledger report
 * GET /api/v1/reports/general-ledger/{pdf|excel}
 */
export async function exportGeneralLedger(
  params: { ledgerId: string; fromDate: string; toDate: string; subledgerId?: string },
  format: ExportFormat
): Promise<void> {
  return downloadExport(
    '/api/v1/reports/general-ledger',
    params,
    format,
    `general-ledger_${params.ledgerId}_${params.fromDate}_to_${params.toDate}`
  )
}

/**
 * Export Aging report (Receivables or Payables)
 * GET /api/v1/reports/aging/{pdf|excel}
 */
export async function exportAgingReport(
  params: {
    type: 'RECEIVABLE' | 'PAYABLE'
    asOfDate?: string
    accountOwnerId?: string
    /** Aging bucket — must match the backend tokens (FE "60+" card → backend "60+"). */
    bucket?: 'current' | '1-30' | '31-60' | '61-90' | 'over90' | '60+'
    purpose?: 'FREIGHT' | 'CUSTOMS_DUTY' | 'ADDITIONAL' | 'OTHER'
    paymentTermsLabel?: string
  },
  format: ExportFormat
): Promise<void> {
  const reportType = params.type === 'RECEIVABLE' ? 'receivables' : 'payables'
  const dateStr = params.asOfDate || new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/reports/aging',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `${reportType}-aging_${dateStr}`
  )
}

/**
 * Export Cash Position report
 * GET /api/v1/reports/cash-position/{pdf|excel}
 */
export async function exportCashPosition(
  params: { asOfDate?: string },
  format: ExportFormat
): Promise<void> {
  const dateStr = params.asOfDate || new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/reports/cash-position',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `cash-position_${dateStr}`
  )
}

/** Format ISO date (YYYY-MM-DD) as DD_MM_YYYY for filenames */
function formatDateForFilename(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  return `${d}_${m}_${y}`
}

/** Sanitize subledger name for use in a filename (strip path-unsafe chars) */
function sanitizeNameForFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '').trim()
}

/**
 * Export Customer Statement
 * GET /api/v1/reports/customer-statement/{subledgerId}/{pdf|excel}
 */
export async function exportCustomerStatement(
  subledgerId: string,
  params: { fromDate: string; toDate: string },
  format: ExportFormat,
  subledgerName: string
): Promise<void> {
  const from = formatDateForFilename(params.fromDate)
  const to = formatDateForFilename(params.toDate)
  const name = sanitizeNameForFilename(subledgerName)
  return downloadExport(
    `/api/v1/reports/customer-statement/${subledgerId}`,
    params,
    format,
    `Statement of Account from ${from} to ${to} - ${name}`
  )
}

/**
 * Export Vendor Statement
 * GET /api/v1/reports/vendor-statement/{subledgerId}/{pdf|excel}
 */
export async function exportVendorStatement(
  subledgerId: string,
  params: { fromDate: string; toDate: string },
  format: ExportFormat,
  subledgerName: string
): Promise<void> {
  const from = formatDateForFilename(params.fromDate)
  const to = formatDateForFilename(params.toDate)
  const name = sanitizeNameForFilename(subledgerName)
  return downloadExport(
    `/api/v1/reports/vendor-statement/${subledgerId}`,
    params,
    format,
    `Statement of Account from ${from} to ${to} - ${name}`
  )
}

// ============================================
// Statement of Account (Outstanding Invoices)
// ============================================

/**
 * Fetch Customer Statement of Account (Outstanding Invoices)
 * GET /api/v1/reports/statement-of-account/customer/{subledgerId}
 */
export async function fetchCustomerStatementOfAccount(
  subledgerId: string,
  params: { asOfDate?: string } = {}
): Promise<StatementOfAccountResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)
  const response = await authFetch<ApiResponse<StatementOfAccountResponse>>(
    `/api/v1/reports/statement-of-account/customer/${subledgerId}${qs}`,
    token
  )
  return response.data
}

/**
 * Fetch Vendor Statement of Account (Outstanding Invoices)
 * GET /api/v1/reports/statement-of-account/vendor/{subledgerId}
 */
export async function fetchVendorStatementOfAccount(
  subledgerId: string,
  params: { asOfDate?: string } = {}
): Promise<StatementOfAccountResponse> {
  const token = getToken()
  const qs = buildQueryString(params as Record<string, unknown>)
  const response = await authFetch<ApiResponse<StatementOfAccountResponse>>(
    `/api/v1/reports/statement-of-account/vendor/${subledgerId}${qs}`,
    token
  )
  return response.data
}

/**
 * Export Customer Statement of Account
 * GET /api/v1/reports/statement-of-account/customer/{subledgerId}/{pdf|excel}
 */
export async function exportCustomerStatementOfAccount(
  subledgerId: string,
  params: { asOfDate: string },
  format: ExportFormat,
  subledgerName: string
): Promise<void> {
  const asOn = formatDateForFilename(params.asOfDate)
  const name = sanitizeNameForFilename(subledgerName)
  return downloadExport(
    `/api/v1/reports/statement-of-account/customer/${subledgerId}`,
    params,
    format,
    `Statement of Account as on ${asOn} - ${name}`
  )
}

/**
 * Export Vendor Statement of Account
 * GET /api/v1/reports/statement-of-account/vendor/{subledgerId}/{pdf|excel}
 */
export async function exportVendorStatementOfAccount(
  subledgerId: string,
  params: { asOfDate: string },
  format: ExportFormat,
  subledgerName: string
): Promise<void> {
  const asOn = formatDateForFilename(params.asOfDate)
  const name = sanitizeNameForFilename(subledgerName)
  return downloadExport(
    `/api/v1/reports/statement-of-account/vendor/${subledgerId}`,
    params,
    format,
    `Statement of Account as on ${asOn} - ${name}`
  )
}

/**
 * Export VAT Report
 * GET /api/v1/reports/vat/{pdf|excel}
 */
export async function exportVatReport(
  params: { fromDate: string; toDate: string },
  format: ExportFormat
): Promise<void> {
  return downloadExport(
    '/api/v1/reports/vat',
    params,
    format,
    `vat-report_${params.fromDate}_to_${params.toDate}`
  )
}

/**
 * Export single voucher as PDF (PDF only, no Excel)
 * GET /api/v1/vouchers/{voucherId}/pdf
 */
export async function exportVoucherPdf(
  voucherId: string,
  voucherNo: string
): Promise<void> {
  const token = getToken()
  const blob = await authFetchBlob(`/api/v1/vouchers/${voucherId}/pdf`, token)
  triggerBlobDownload(blob, `voucher_${voucherNo}.pdf`)
}

// ============================================
// Invoice Layer APIs
// Per Invoice-Layer-Frontend-Integration-Guide.md
// ============================================

// ============================================
// Invoice CRUD
// ============================================

/**
 * Fetch paginated list of invoices
 * GET /api/v1/invoices
 */
export async function fetchInvoices(
  filters: InvoiceFilters = {}
): Promise<PagedResponse<InvoiceListItem>> {
  const token = getToken()
  const qs = buildQueryString(filters as Record<string, unknown>)

  const response = await authFetch<ApiResponse<PagedResponse<InvoiceListItem>>>(
    `/api/v1/invoices${qs}`,
    token
  )

  return response.data
}

/**
 * Raw API response structure for invoice summary
 */
interface InvoiceSummaryApiResponse {
  salesInvoices: {
    draft: { count: number; amount: number }
    sent: { count: number; amount: number }
    posted: { count: number; amount: number }
    paid: { count: number; amount: number }
    total: { count: number; amount: number }
  }
  purchaseInvoices: {
    draft: { count: number; amount: number }
    sent: { count: number; amount: number }
    posted: { count: number; amount: number }
    paid: { count: number; amount: number }
    total: { count: number; amount: number }
  }
  currency?: string
}

/**
 * Fetch invoice summary for pipeline cards / dashboard
 * GET /api/v1/invoices/summary
 */
export async function fetchInvoiceSummary(): Promise<InvoiceSummary> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceSummaryApiResponse>>(
    '/api/v1/invoices/summary',
    token
  )

  const raw = response.data

  // Transform API response to expected frontend structure
  return {
    salesInvoices: {
      draftCount: raw.salesInvoices?.draft?.count ?? 0,
      sentCount: raw.salesInvoices?.sent?.count ?? 0,
      paidCount: raw.salesInvoices?.paid?.count ?? 0,
      totalOutstanding: raw.salesInvoices?.sent?.amount ?? 0,
    },
    purchaseInvoices: {
      draftCount: raw.purchaseInvoices?.draft?.count ?? 0,
      postedCount: raw.purchaseInvoices?.posted?.count ?? 0,
    },
    currency: raw.currency ?? getTenantMoney().currency,
  }
}

/**
 * Fetch invoice detail by ID
 * GET /api/v1/invoices/{id}
 */
export async function fetchInvoiceDetail(id: string): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    `/api/v1/invoices/${id}`,
    token
  )

  return response.data
}

/**
 * Fetch invoice by invoice number
 * GET /api/v1/invoices/by-invoice-no/{invoiceNo}
 */
export async function fetchInvoiceByNumber(invoiceNo: string): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    `/api/v1/invoices/by-invoice-no/${encodeURIComponent(invoiceNo)}`,
    token
  )

  return response.data
}

/**
 * Fetch invoices by job order ID
 * GET /api/v1/invoices/by-job-order/{jobOrderId}
 */
export async function fetchInvoicesByJobOrder(
  jobOrderId: string
): Promise<InvoiceListItem[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceListItem[]>>(
    `/api/v1/invoices/by-job-order/${jobOrderId}`,
    token
  )

  return response.data
}

/**
 * Preview next invoice number
 * GET /api/v1/invoices/preview-next-number?invoiceType={type}
 */
export async function previewNextInvoiceNumber(
  invoiceType: 'SALES' | 'PURCHASE'
): Promise<{ nextInvoiceNo: string }> {
  const token = getToken()

  const response = await authFetch<ApiResponse<{ nextInvoiceNo: string }>>(
    `/api/v1/invoices/preview-next-number?invoiceType=${invoiceType}`,
    token
  )

  return response.data
}

/**
 * Create Sales Invoice from Job Order
 * POST /api/v1/invoices/from-job-order
 */
export async function createSalesInvoiceFromJO(
  data: CreateSalesInvoiceFromJORequest
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    '/api/v1/invoices/from-job-order',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

export async function fetchPurchaseAllocationOptions(jobOrderIds: string[]): Promise<import('@/features/finance/types').PurchaseAllocationOptions[]> {
  const result: import('@/features/finance/types').PurchaseAllocationOptions[] = []
  for (let offset = 0; offset < jobOrderIds.length; offset += 200) {
    const response = await authFetch<ApiResponse<import('@/features/finance/types').PurchaseAllocationOptions[]>>(
      '/api/v1/job-orders/purchase-allocation-options', getToken(),
      { method: 'POST', body: JSON.stringify({ jobOrderIds: jobOrderIds.slice(offset, offset + 200) }) })
    result.push(...response.data)
  }
  return result
}

/**
 * Create Purchase Invoice
 * POST /api/v1/invoices
 */
export async function createPurchaseInvoice(
  data: CreatePurchaseInvoiceRequest
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    '/api/v1/invoices',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, invoiceType: 'PURCHASE' }),
    }
  )

  return response.data
}

/** Replace the complete, revision-guarded allocation set on a GENERIC_JOB_V1 invoice. */
export async function replaceInvoiceJobAllocations(
  invoiceId: string,
  data: ReplaceInvoiceJobAllocationsRequest
): Promise<ReplaceInvoiceJobAllocationsResponse> {
  const token = getToken()
  const response = await authFetch<ApiResponse<ReplaceInvoiceJobAllocationsResponse>>(
    `/api/v1/invoices/${encodeURIComponent(invoiceId)}/job-allocations`,
    token,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }
  )
  return response.data
}

export async function fetchInvoiceJobAllocations(
  invoiceId: string,
  page = 1,
  size = 100
): Promise<PagedResponse<InvoiceJobAllocation>> {
  const token = getToken()
  const response = await authFetch<ApiResponse<PagedResponse<InvoiceJobAllocation>>>(
    `/api/v1/invoices/${encodeURIComponent(invoiceId)}/job-allocations?page=${page}&size=${size}`,
    token
  )
  return response.data
}

/**
 * Create a Standalone Sales Invoice (no Job Order link) — for utility billings,
 * warehousing, gate pass, BOE, etc. POSTs to the generic POST /api/v1/invoices
 * endpoint with `invoiceType=SALES`. Distinct from `createSalesInvoiceFromJO`,
 * which fetches a JO and auto-builds line items from its pricing.
 */
export async function createStandaloneSalesInvoice(
  data: CreateStandaloneSalesInvoiceRequest
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    '/api/v1/invoices',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, invoiceType: 'SALES' }),
    }
  )

  return response.data
}

/**
 * The reason Finance gave for refusing a combined customs duty invoice, when it gave it as a field
 * refusal: the 400's `message` is then only a headline ("Validation failed for field 'jobOrders'")
 * and the sentence ("Job JO-2026-09001 is cancelled") is in the body's `data`. Null for any other
 * failure — a 409 already says which invoice holds the job in its `message`, and its `data` holds
 * ids, not words.
 */
export function combinedCustomsRefusalReason(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 400 || !/^Validation failed/.test(err.message)) return null
  const fields = (err.data as { data?: unknown } | null | undefined)?.data
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return null
  const reasons = Object.values(fields as Record<string, unknown>)
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim())
  return reasons.length > 0 ? reasons.join(' ') : null
}

/**
 * Create one Customs Duty Sales Invoice for several jobs of one customer (Finance #109).
 * POST /api/v1/invoices/customs-duty/combined — each job carries its own charges.
 * A refusal is thrown with Finance's own sentence as its message (combinedCustomsRefusalReason).
 */
export async function createCombinedCustomsInvoice(
  data: CreateCombinedCustomsInvoiceRequest
): Promise<InvoiceDetail> {
  const token = getToken()

  try {
    const response = await authFetch<ApiResponse<InvoiceDetail>>(
      '/api/v1/invoices/customs-duty/combined',
      token,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    )

    return response.data
  } catch (err) {
    const reason = combinedCustomsRefusalReason(err)
    throw reason && err instanceof ApiError ? new ApiError(reason, err.status, err.data) : err
  }
}

/**
 * For each job: the live customs duty invoice already holding it, and the duty on the carrier's bill.
 * POST /api/v1/invoices/customs-duty/candidates — one entry per distinct id, in request order.
 */
export async function fetchCustomsDutyCandidates(
  jobOrderIds: string[]
): Promise<CustomsDutyCandidate[]> {
  const token = getToken()

  const response = await authFetch<ApiResponse<CustomsDutyCandidate[]>>(
    '/api/v1/invoices/customs-duty/candidates',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobOrderIds }),
    }
  )

  return response.data ?? []
}

/**
 * A customer's express and freight jobs that can go on a customs duty invoice (not cancelled,
 * last 120 days, newest first). GET /api/v1/job-orders/customs-invoice-candidates?customerId=
 */
export async function fetchCustomsInvoiceJobCandidates(
  customerId: string
): Promise<CustomsInvoiceJobCandidatesResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<CustomsInvoiceJobCandidatesResponse>>(
    `/api/v1/job-orders/customs-invoice-candidates?customerId=${encodeURIComponent(customerId)}`,
    token
  )

  return response.data
}

/**
 * The given jobs, in request order, with their customer and status (cancelled jobs included, so the
 * screen can name them). POST /api/v1/job-orders/customs-invoice-candidates/by-ids
 */
export async function fetchCustomsInvoiceJobsByIds(
  jobOrderIds: string[]
): Promise<CustomsInvoiceJobCandidatesResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<CustomsInvoiceJobCandidatesResponse>>(
    '/api/v1/job-orders/customs-invoice-candidates/by-ids',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobOrderIds }),
    }
  )

  return response.data
}

/**
 * Update Invoice (DRAFT only)
 * PUT /api/v1/invoices/{id}
 */
export async function updateInvoice(
  id: string,
  data: UpdateInvoiceRequest
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    `/api/v1/invoices/${id}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Delete Invoice (DRAFT only)
 * DELETE /api/v1/invoices/{id}
 */
export async function deleteInvoice(id: string): Promise<null> {
  const token = getToken()

  const response = await authFetch<ApiResponse<null>>(
    `/api/v1/invoices/${id}`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Invoice Actions
// ============================================

/**
 * Send Sales Invoice (DRAFT -> SENT)
 * POST /api/v1/invoices/{id}/send
 */
export async function sendInvoice(
  id: string,
  remarks?: string
): Promise<InvoiceActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceActionResponse>>(
    `/api/v1/invoices/${id}/send`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: remarks ? JSON.stringify({ remarks }) : undefined,
    }
  )

  return response.data
}

/**
 * Get pre-populated email draft for a Sales Invoice
 * GET /api/v1/invoices/{id}/email-draft
 *
 * `threadPreview` (ticket #114) — only the Send Email dialog sets it: the backend then says whether
 * the invoice goes as a reply on the job's email conversation, with that reply's subject and the
 * customer's copied colleagues on CC.
 */
export async function fetchInvoiceEmailDraft(
  id: string,
  options?: { threadPreview?: boolean }
): Promise<InvoiceEmailDraft> {
  const token = getToken()
  const query = options?.threadPreview ? '?threadPreview=true' : ''

  const response = await authFetch<ApiResponse<InvoiceEmailDraft>>(
    `/api/v1/invoices/${id}/email-draft${query}`,
    token
  )

  return response.data
}

/**
 * Send Sales Invoice via email (DRAFT -> SENT)
 * POST /api/v1/invoices/{id}/send-email
 */
export async function sendInvoiceEmail(
  id: string,
  payload: SendInvoiceEmailRequest
): Promise<InvoiceActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceActionResponse>>(
    `/api/v1/invoices/${id}/send-email`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  )

  return response.data
}

/**
 * Post Purchase Invoice (DRAFT -> POSTED)
 * POST /api/v1/invoices/{id}/post
 */
export async function postInvoice(
  id: string,
  remarks?: string
): Promise<InvoiceActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceActionResponse>>(
    `/api/v1/invoices/${id}/post`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: remarks ? JSON.stringify({ remarks }) : undefined,
    }
  )

  return response.data
}

/**
 * Revert SENT Sales Invoice to DRAFT for editing
 * POST /api/v1/invoices/{id}/revert-to-draft
 */
export async function revertInvoiceToDraft(
  id: string,
  remarks?: string
): Promise<InvoiceActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceActionResponse>>(
    `/api/v1/invoices/${id}/revert-to-draft`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: remarks ? JSON.stringify({ remarks }) : undefined,
    }
  )

  return response.data
}

/**
 * Re-pull customer/vendor details from master-data and overwrite the
 * invoice's partySnapshot. Allowed in any status except CANCELLED.
 * For non-DRAFT invoices the change is recorded as an InvoiceAmendment.
 * POST /api/v1/invoices/{id}/refresh-customer-details
 */
export async function refreshInvoiceCustomerDetails(
  id: string
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    `/api/v1/invoices/${id}/refresh-customer-details`,
    token,
    {
      method: 'POST',
    }
  )

  return response.data
}

/**
 * Preview how many of a customer's unpaid (SENT + PARTIALLY_PAID) sales
 * invoices a bulk payment-terms refresh would affect. Used to decide whether
 * to prompt the user after the customer's credit period changes.
 * GET /api/v1/invoices/refresh-terms/preview?customerId={id}
 */
export async function previewRefreshInvoiceTerms(
  customerId: string
): Promise<RefreshTermsPreviewResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<RefreshTermsPreviewResponse>>(
    `/api/v1/invoices/refresh-terms/preview?customerId=${encodeURIComponent(customerId)}`,
    token
  )

  return response.data
}

/**
 * Bulk-refresh payment terms + due dates on a customer's unpaid sales invoices
 * after the customer's credit period changed. Each refreshed invoice records
 * its own amendment audit entry; paid/cancelled invoices and credit notes are
 * left untouched.
 * POST /api/v1/invoices/refresh-terms?customerId={id}
 */
export async function refreshInvoiceTerms(
  customerId: string
): Promise<RefreshTermsResult> {
  const token = getToken()

  const response = await authFetch<ApiResponse<RefreshTermsResult>>(
    `/api/v1/invoices/refresh-terms?customerId=${encodeURIComponent(customerId)}`,
    token,
    {
      method: 'POST',
    }
  )

  return response.data
}

/**
 * Mark Sales Invoice as Paid (SENT -> PAID)
 * POST /api/v1/invoices/{id}/mark-paid
 *
 * This endpoint now creates a Receipt Voucher and Allocation automatically.
 * Payment details (payment method, date, reference) are required.
 */
export async function markInvoicePaid(
  id: string,
  data: MarkInvoicePaidRequest
): Promise<MarkInvoicePaidResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<MarkInvoicePaidResponse>>(
    `/api/v1/invoices/${id}/mark-paid`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Cancel Invoice (DRAFT only)
 * POST /api/v1/invoices/{id}/cancel
 */
export async function cancelInvoice(
  id: string,
  reason: string
): Promise<InvoiceActionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceActionResponse>>(
    `/api/v1/invoices/${id}/cancel`,
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  )

  return response.data
}

/**
 * Create Credit Note
 * POST /api/v1/invoices/credit-note
 */
export async function createCreditNote(
  data: CreateCreditNoteRequest
): Promise<InvoiceDetail> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDetail>>(
    '/api/v1/invoices/credit-note',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

// ============================================
// Invoice PDF
// ============================================

/**
 * Download Invoice PDF
 * GET /api/v1/invoices/{id}/pdf
 */
export async function exportInvoicePdf(
  invoiceId: string,
  invoiceNo: string,
  partyName?: string
): Promise<void> {
  const token = getToken()
  const blob = await authFetchBlob(`/api/v1/invoices/${invoiceId}/pdf`, token)
  const filename = partyName
    ? `${invoiceNo} - ${sanitizeNameForFilename(partyName)}.pdf`
    : `invoice_${invoiceNo}.pdf`
  triggerBlobDownload(blob, filename)
}

/**
 * Get Invoice PDF URL for preview (inline)
 * GET /api/v1/invoices/{id}/pdf/preview
 * Returns blob URL for embedding in iframe
 */
export async function getInvoicePdfPreviewUrl(invoiceId: string): Promise<string> {
  const token = getToken()
  const blob = await authFetchBlob(`/api/v1/invoices/${invoiceId}/pdf/preview`, token)
  return URL.createObjectURL(blob)
}

/**
 * Download Purchase Invoice Excel cost analysis.
 * GET /api/v1/invoices/{id}/excel?format=detailed|summary
 */
export async function exportInvoiceExcel(
  invoiceId: string,
  invoiceNo: string,
  format: 'detailed' | 'summary' = 'detailed'
): Promise<void> {
  const token = getToken()
  const blob = await authFetchBlob(`/api/v1/invoices/${invoiceId}/excel?format=${format}`, token)
  const suffix = format === 'summary' ? 'summary' : 'detailed'
  triggerBlobDownload(blob, `${invoiceNo}-cost-analysis-${suffix}.xlsx`)
}

/** Same selected-document result used by both PI workbook formats. */
export async function fetchSelectedPurchaseMoney(invoiceId: string): Promise<SelectedPurchaseMoney> {
  const response = await authFetch<ApiResponse<SelectedPurchaseMoney>>(
    `/api/v1/invoices/${encodeURIComponent(invoiceId)}/cost-analysis`, getToken()
  )
  return response.data
}

// `fetchJobOrderReconciliation` (GET /api/v1/job-orders/{jobOrderId}/reconciliation) was
// removed along with the drawer that was its only caller. That drawer had no importers, threw
// on a null cost variance if it was ever mounted, and read two fields the backend does not
// send. The endpoint itself still exists and is now resolver-backed; if a UI for it is ever
// built, type it off the current response shape rather than restoring the old one.

/**
 * Update actual chargeable weight for a job order
 * PUT /api/v1/job-orders/{jobOrderId}/actual-weight
 *
 * @param dryRun - If true, returns preview without saving to DB
 */
export async function updateActualWeight(
  jobOrderId: string,
  data: UpdateActualWeightRequest,
  dryRun = false
): Promise<UpdateActualWeightResponse> {
  const token = getToken()

  const queryString = dryRun ? '?dryRun=true' : ''
  const response = await authFetch<ApiResponse<UpdateActualWeightResponse>>(
    `/api/v1/job-orders/${jobOrderId}/actual-weight${queryString}`,
    token,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  )

  return response.data
}

/**
 * Search job orders (for PI linking)
 * GET /api/v1/job-orders/search?q={query}&limit={limit}
 */
export async function searchJobOrders(
  query: string,
  limit: number = 20
): Promise<{ results: JobOrderSearchResult[]; totalCount: number; searchTerm: string }> {
  const token = getToken()

  const response = await authFetch<ApiResponse<{
    results: JobOrderSearchResult[]
    totalCount: number
    searchTerm: string
  }>>(
    `/api/v1/job-orders/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    token
  )

  return response.data
}

/**
 * Fetch customer's payment terms days for due date calculation.
 * Returns the structured paymentTermsDays from the customer's financials,
 * or null if the customer has no configured value.
 * GET /api/v1/customers/{customerId}
 */
export async function fetchCustomerPaymentTermsDays(
  customerId: string
): Promise<number | null> {
  const token = getToken()
  try {
    const response = await authFetch<ApiResponse<{
      financials?: { paymentTermsDays?: number | null }
    }>>(
      `/api/v1/customers/${customerId}`,
      token
    )
    return response.data?.financials?.paymentTermsDays ?? null
  } catch {
    return null
  }
}

/**
 * Fetch Job Order pricing details for invoice creation
 * Uses the main JO detail endpoint and extracts pricing snapshot
 * GET /api/v1/job-orders/{jobOrderId}
 */
export async function fetchJobOrderPricing(
  jobOrderId: string
): Promise<{
  jobOrderId: string
  customerName: string
  customerId: string
  customerLineItems: Array<{
    chargeTypeId: string | null
    description: string
    amount: number
    vatPercent: number
    vatAmount: number
  }>
  totalSellingPrice: number
  currency: string
}> {
  const token = getToken()

  // Fetch full JO detail and extract pricing
  const response = await authFetch<ApiResponse<{
    jobOrderId: string
    customerSnapshot?: {
      customerId: string
      customerName: string
    }
    pricingSnapshot?: {
      customerLineItems?: Array<{
        chargeTypeId: string | null
        label: string
        sellingPrice: number
        vatPercent: number | null
        vatAmount: number | null
      }>
      totalSellingPrice: number
      currency: string
    }
    // Recalculated pricing after weight update (takes priority if present)
    recalculatedPricing?: {
      customerLineItems?: Array<{
        chargeTypeId: string | null
        label: string
        sellingPrice: number
        vatPercent: number | null
        vatAmount: number | null
      }>
      totalSellingPrice: number
    }
  }>>(
    `/api/v1/job-orders/${jobOrderId}`,
    token
  )

  const jo = response.data
  // Use recalculatedPricing if available, otherwise fall back to pricingSnapshot
  const hasRecalculatedPricing = jo.recalculatedPricing && jo.recalculatedPricing.customerLineItems && jo.recalculatedPricing.customerLineItems.length > 0
  const activePricing = hasRecalculatedPricing ? jo.recalculatedPricing : jo.pricingSnapshot
  const currency = jo.pricingSnapshot?.currency ?? getTenantMoney().currency

  // Transform customer line items to the expected format
  const customerLineItems = activePricing?.customerLineItems?.map(item => ({
    chargeTypeId: item.chargeTypeId,
    description: item.label,
    amount: item.sellingPrice,
    vatPercent: item.vatPercent ?? 0,
    vatAmount: item.vatAmount ?? 0,
  })) ?? []

  return {
    jobOrderId: jo.jobOrderId,
    customerName: jo.customerSnapshot?.customerName ?? '',
    customerId: jo.customerSnapshot?.customerId ?? '',
    customerLineItems,
    totalSellingPrice: activePricing?.totalSellingPrice ?? 0,
    currency,
  }
}

/**
 * One planned-cost line of a Job Order, as the Purchase Invoice screens read it. `cost` is in the
 * currency the carrier quoted (`currency`; null is the job's own currency), and
 * `amountInDisplayCurrency` is that cost in the job's currency at the rate frozen at the quote, so a
 * USD 1,178.20 line carries AED 4,341.67 (Job Order Q3 #18). A screen working in the job's currency
 * reads it through `plannedCostInBase`. The FX fields are absent on a line in the job's currency, and
 * from a Job Order endpoint that does not send them.
 */
export interface JobOrderPlannedCostLine {
  name: string
  cost: number
  currency?: string | null
  fxRate?: number | null
  amountInDisplayCurrency?: number | null
}

/**
 * One charge-type mapping of a Job Order's plan: `cost` is in the line's own currency (as its planned
 * line), `profit` and `sellingPrice` in the job's. Its job-currency cost is `cost` at `fxRate`.
 */
export interface JobOrderPlannedCostMapping {
  lineIndex: number
  rawItemName: string
  cost: number
  profit: number
  sellingPrice: number
  currency?: string | null
  fxRate?: number | null
}

/**
 * One customs charge line from the JO's customsPricing snapshot. `cost` is null when the Customs SI
 * line recorded none (unknown, not zero), and `profit` is then null too (Job Order's
 * `CustomsPricingLineItem`, wave 7b-10).
 */
export interface JobOrderCustomsPricingLineItem {
  description: string
  cost: number | null
  profit: number | null
  sellingPrice: number
}

/**
 * Fetch Job Order planned cost line items for Purchase Invoice creation.
 * Extracts plannedCostLineItems and chargeTypeMappings (for per-line profit)
 * from pricingSnapshot (or recalculatedPricing). Each line keeps its own currency
 * and rate; `currency` is the job's.
 * GET /api/v1/job-orders/{jobOrderId}
 */
export async function fetchJobOrderPlannedCosts(
  jobOrderId: string
): Promise<{
  jobOrderId: string
  plannedCostLineItems: JobOrderPlannedCostLine[]
  chargeTypeMappings: JobOrderPlannedCostMapping[]
  totalPlannedCost: number
  totalSellingPrice: number
  currency: string
  /**
   * Customs Duty pricing snapshot synced from the JO's Customs SI. Used to
   * prefill a Customs Purchase Invoice's cost lines. Empty when the JO has no
   * Customs SI yet.
   */
  customsLineItems: JobOrderCustomsPricingLineItem[]
}> {
  const token = getToken()

  const response = await authFetch<ApiResponse<{
    jobOrderId: string
    pricingSnapshot?: {
      plannedCostLineItems?: JobOrderPlannedCostLine[]
      chargeTypeMappings?: JobOrderPlannedCostMapping[]
      totalPlannedCost?: number
      totalSellingPrice?: number
      currency?: string
    }
    recalculatedPricing?: {
      plannedCostLineItems?: JobOrderPlannedCostLine[]
      totalPlannedCost?: number
      totalSellingPrice?: number
    }
    customsPricing?: {
      lineItems?: JobOrderCustomsPricingLineItem[]
    }
  }>>(
    `/api/v1/job-orders/${jobOrderId}`,
    token
  )

  const jo = response.data
  const hasRecalculated = jo.recalculatedPricing?.plannedCostLineItems &&
    jo.recalculatedPricing.plannedCostLineItems.length > 0
  const activePricing = hasRecalculated ? jo.recalculatedPricing : jo.pricingSnapshot

  return {
    jobOrderId: jo.jobOrderId,
    plannedCostLineItems: activePricing?.plannedCostLineItems ?? [],
    chargeTypeMappings: jo.pricingSnapshot?.chargeTypeMappings ?? [],
    totalPlannedCost: activePricing?.totalPlannedCost ?? 0,
    totalSellingPrice: activePricing?.totalSellingPrice ?? jo.pricingSnapshot?.totalSellingPrice ?? 0,
    currency: jo.pricingSnapshot?.currency ?? getTenantMoney().currency,
    customsLineItems: jo.customsPricing?.lineItems ?? [],
  }
}

export interface PIPlannedCostsForJo {
  plannedCostLineItems: JobOrderPlannedCostLine[]
  chargeTypeMappings: JobOrderPlannedCostMapping[]
  totalPlannedCost: number
  totalSellingPrice: number
  currency: string
}

/**
 * Batched version of fetchJobOrderPlannedCosts. Hits POST /api/v1/job-orders/batch-pi-export-data
 * which returns one PIExportJobOrderSummary per JO in a single round-trip — used by the PI
 * detail drawer to enrich line items with planned vs actual data without firing N parallel
 * GETs (one per linked JO).
 *
 * Returns an empty map if no IDs are passed.
 */
export async function batchFetchJobOrderPlannedCosts(
  jobOrderIds: string[]
): Promise<Map<string, PIPlannedCostsForJo>> {
  if (jobOrderIds.length === 0) return new Map()
  const token = getToken()

  const response = await authFetch<ApiResponse<Record<string, {
    jobOrderId: string
    totalSellingPrice?: number
    totalPlannedCost?: number
    plannedProfit?: number
    currency?: string
    plannedCostLineItems?: JobOrderPlannedCostLine[]
    chargeTypeMappings?: JobOrderPlannedCostMapping[]
  }>>>(
    '/api/v1/job-orders/batch-pi-export-data',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jobOrderIds),
    }
  )

  const out = new Map<string, PIPlannedCostsForJo>()
  const data = response.data ?? {}
  for (const [joId, entry] of Object.entries(data)) {
    out.set(joId, {
      plannedCostLineItems: entry.plannedCostLineItems ?? [],
      chargeTypeMappings: entry.chargeTypeMappings ?? [],
      totalPlannedCost: entry.totalPlannedCost ?? 0,
      totalSellingPrice: entry.totalSellingPrice ?? 0,
      currency: entry.currency ?? getTenantMoney().currency,
    })
  }
  return out
}

// ============================================
// Invoice Exports
// ============================================

/**
 * Export invoices list
 * GET /api/v1/invoices/{pdf|excel}
 */
export async function exportInvoices(
  params: InvoiceFilters,
  format: ExportFormat
): Promise<void> {
  const dateStr = new Date().toISOString().split('T')[0]
  return downloadExport(
    '/api/v1/invoices',
    params as Record<string, string | number | boolean | undefined>,
    format,
    `invoices-list_${dateStr}`
  )
}

// ============================================
// Vendor PDF (Purchase Invoice)
// ============================================

/**
 * Upload a vendor PDF for a Purchase Invoice.
 * Uses FormData (not authFetch) because multipart/form-data requires browser to set boundary.
 *
 * POST /api/v1/invoices/{id}/vendor-pdf
 */
export async function uploadVendorPdf(
  invoiceId: string,
  file: File
): Promise<InvoiceDocumentResponse> {
  const token = getToken()
  const userId = useAuthStore.getState().user?.userId

  const formData = new FormData()
  formData.append('file', file)

  const url = `${API_BASE_URL}/api/v1/invoices/${invoiceId}/vendor-pdf`

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  }
  if (userId) {
    headers['X-User-Id'] = userId
  }
  if (API_BASE_URL.includes('ngrok')) {
    headers['ngrok-skip-browser-warning'] = 'true'
  }
  if (API_BASE_URL.includes('zrok')) {
    headers['skip_zrok_interstitial'] = 'TRUE'
  }

  let response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  })

  // On 401, attempt a silent token refresh and retry once before surfacing
  // expiry (in-place re-auth modal mid-session, or logout on fresh load).
  if (response.status === 401) {
    const store = useAuthStore.getState()
    if (!store.sessionExpired) {
      const newToken = await store.refreshSession()
      if (newToken) {
        response = await fetch(url, {
          method: 'POST',
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
          body: formData,
        })
      } else {
        store.handleAuthExpiry()
      }
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new ApiError(
      errorData?.message || `Upload failed with status ${response.status}`,
      response.status,
      errorData
    )
  }

  const data = await response.json() as ApiResponse<InvoiceDocumentResponse>
  return data.data
}

/**
 * Download the vendor PDF for a Purchase Invoice.
 *
 * GET /api/v1/invoices/{id}/vendor-pdf
 */
export async function downloadVendorPdf(
  invoiceId: string,
  fileName?: string
): Promise<void> {
  const token = getToken()
  const blob = await authFetchBlob(`/api/v1/invoices/${invoiceId}/vendor-pdf`, token)
  triggerBlobDownload(blob, fileName || `vendor-invoice-${invoiceId}.pdf`)
}

/**
 * Delete the vendor PDF for a Purchase Invoice.
 *
 * DELETE /api/v1/invoices/{id}/vendor-pdf
 */
export async function deleteVendorPdf(invoiceId: string): Promise<InvoiceDocumentResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<InvoiceDocumentResponse>>(
    `/api/v1/invoices/${invoiceId}/vendor-pdf`,
    token,
    { method: 'DELETE' }
  )

  return response.data
}

// ============================================
// Purchase Invoice PDF Extraction (AI-powered)
// ============================================

/**
 * Extract purchase invoice data from a vendor PDF using AI.
 * Converts PDF to images, sends to Qwen via OpenRouter, extracts
 * shipment data, and matches AWBs to Job Orders.
 *
 * POST /api/v1/conversions/purchase-invoice/extract
 */
export async function extractPurchaseInvoicePdf(
  pdfBase64: string
): Promise<PIExtractionResponse> {
  const token = getToken()

  const response = await authFetch<ApiResponse<PIExtractionResponse>>(
    '/api/v1/conversions/purchase-invoice/extract',
    token,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdfBase64 }),
    }
  )

  return response.data
}

/**
 * Carriers for which finance-service has a deterministic, carrier-specific
 * PDF parser. Only carriers in this union have a parser; others must use
 * the AI extraction path.
 */
export type PIExtractionCarrier = 'DHL' | 'FEDEX' | 'UPS'

/**
 * Extract a vendor invoice PDF using a deterministic carrier-specific
 * parser in finance-service. More accurate than AI extraction for known
 * carriers (e.g. DHL Express tax invoices), but only available for carriers
 * we've built a parser for.
 *
 * Response shape is identical to `extractPurchaseInvoicePdf` so callers
 * consume both paths uniformly. On 422 (RECONCILIATION_FAILED or
 * UNRECOGNIZED_LAYOUT), throw an `ApiError` whose `data` includes
 * structured details and an `errorCode` of `INVOICE_EXTRACTION_FAILED`.
 * On 501 (UNSUPPORTED_CARRIER), the caller should redirect the user to AI
 * extraction.
 *
 * POST /api/v1/invoices/extract?carrier=DHL   (multipart/form-data, field: `file`)
 */
export async function extractPurchaseInvoicePdfByCarrier(
  carrier: PIExtractionCarrier,
  file: File
): Promise<PIExtractionResponse> {
  const token = getToken()
  const formData = new FormData()
  formData.append('file', file)

  const response = await authFetch<ApiResponse<PIExtractionResponse>>(
    `/api/v1/invoices/extract?carrier=${carrier}`,
    token,
    {
      method: 'POST',
      body: formData,
    }
  )

  return response.data
}

// ============================================
// SOA Auto-Send (manual triggers + history + PDF download)
// ============================================

/**
 * Mirror types for the backend SOA endpoints (finance-service).
 * These are intentionally narrow (only fields the UI consumes).
 */
export type SoaTriggerType = 'SCHEDULED' | 'MANUAL_TENANT_RUN' | 'MANUAL_PER_CUSTOMER'

export type SoaSendStatus =
  | 'DISPATCHED'
  | 'FAILED'
  | 'SKIPPED_DEDUP'
  | 'SKIPPED_NO_RECIPIENT'
  | 'SKIPPED_NO_OUTSTANDING'
  | 'AUTH_FAILED'

export interface SoaRecipients {
  to: string[]
  cc: string[]
  bcc: string[]
}

export interface SoaSendLogSummary {
  id: string
  customerId: string
  subledgerId: string
  customerNameSnapshot: string
  currencyCode: string
  asOfDate: string
  triggeredBy: SoaTriggerType
  triggeredByUserId: string | null
  status: SoaSendStatus
  errorMessage: string | null
  outstandingTotal: number
  overdueTotal: number
  recipients: SoaRecipients
  pdfAvailable: boolean
  pdfPurgedAt: string | null
  createdAt: string
  /** The statement's email conversation (#107); null when none was stored. */
  emailThreadId?: string | null
  /** When that conversation started. */
  threadStartedAt?: string | null
  /** When the customer replied to this statement. */
  customerRepliedAt?: string | null
}

export interface SoaSendLogEntry {
  voucherId: string
  voucherNo: string
  voucherDate: string
  invoiceNo: string | null
  referenceNo: string | null
  dueDate: string | null
  daysOverdue: number
  invoiceAmount: number
  paidAmount: number
  balance: number
  agingBucket: string
}

export interface SoaAgingBuckets {
  current: number
  days1to30: number
  days31to60: number
  days61to90: number
  over90: number
  total: number
}

export interface SoaSendLogDetail {
  id: string
  customerId: string
  subledgerId: string
  customerNameSnapshot: string
  currencyCode: string
  asOfDate: string
  triggeredBy: SoaTriggerType
  triggeredByUserId: string | null
  status: SoaSendStatus
  errorMessage: string | null
  outstandingTotal: number
  overdueTotal: number
  agingBuckets: SoaAgingBuckets
  recipients: SoaRecipients
  entries: SoaSendLogEntry[]
  pdfAvailable: boolean
  pdfFilename: string
  pdfPurgedAt: string | null
  emailAccountId: string | null
  createdAt: string
  updatedAt: string
  emailThreadId?: string | null
  threadStartedAt?: string | null
  customerRepliedAt?: string | null
}

export interface SoaPerCustomerSendResult {
  currencyCode: string
  status: SoaSendStatus
  logId: string
}

export interface SoaPerCustomerSummary {
  customerId: string
  results: SoaPerCustomerSendResult[]
}

export interface SoaTenantRunSummary {
  asOfDate: string
  triggeredBy: string
  enabled: boolean
  authFailed?: boolean
  enqueued?: number
  skippedDedup?: number
  skippedNoRecipient?: number
  skippedAlreadyQueued?: number
  message?: string | null
}

/**
 * Tenant-level "Run now" — fires the SOA enqueue phase immediately for all
 * eligible customers. Uses the same staggered queue as the scheduled cron.
 *
 * POST /api/v1/soa/auto-send/run-now
 */
export async function runSoaTenantNow(asOfDate?: string): Promise<SoaTenantRunSummary> {
  const token = getToken()
  const response = await authFetch<ApiResponse<SoaTenantRunSummary>>(
    '/api/v1/soa/auto-send/run-now',
    token,
    {
      method: 'POST',
      body: JSON.stringify(asOfDate ? { asOfDate } : {}),
    },
  )
  return response.data
}

/**
 * Per-customer "Send SOA now" — sends one email per outstanding currency
 * (or only the pinned currency). Bypasses dedup; respects the configured
 * SOA email account.
 *
 * POST /api/v1/soa/auto-send/customer/{customerId}
 */
export async function sendSoaForCustomer(
  customerId: string,
  options: { asOfDate?: string; currencyCode?: string; startNewThread?: boolean } = {},
): Promise<SoaPerCustomerSummary> {
  const token = getToken()
  const body: Record<string, unknown> = {}
  if (options.asOfDate) body.asOfDate = options.asOfDate
  if (options.currencyCode) body.currencyCode = options.currencyCode
  // Always explicit: finance reads bodies with Jackson 3, which ignores Kotlin
  // defaults, so a left-out flag failed every unticked send on older backends.
  body.startNewThread = options.startNewThread === true
  const response = await authFetch<ApiResponse<SoaPerCustomerSummary>>(
    `/api/v1/soa/auto-send/customer/${encodeURIComponent(customerId)}`,
    token,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
  return response.data
}

/**
 * A customer's statement email conversation (#107), read through finance so
 * `soa-auto-send:view` is enough. With `threadId`: that conversation (one a
 * statement of this customer went on). Otherwise the current one — for
 * `currencyCode`, or the newest in any currency. Resolves null when there is
 * no conversation yet.
 *
 * GET /api/v1/soa/customers/{customerId}/conversation?currency=&threadId=
 */
export async function fetchSoaConversation(
  customerId: string,
  options: { currencyCode?: string; threadId?: string } = {},
): Promise<EmailThreadResponse | null> {
  const token = getToken()
  const qs = buildQueryString({
    currency: options.currencyCode,
    threadId: options.threadId,
  })
  const response = await authFetch<ApiResponse<EmailThreadResponse | null>>(
    `/api/v1/soa/customers/${encodeURIComponent(customerId)}/conversation${qs}`,
    token,
  )
  return response.data ?? null
}

/**
 * Paginated SOA send history for one customer (Statement-tab table).
 *
 * GET /api/v1/soa/send-logs?customerId=...
 */
export async function fetchSoaSendLogs(
  customerId: string,
  params: { page?: number; size?: number } = {},
): Promise<PagedResponse<SoaSendLogSummary>> {
  const token = getToken()
  const qs = buildQueryString({
    customerId,
    page: params.page ?? 0,
    size: params.size ?? 20,
  })
  const response = await authFetch<ApiResponse<PagedResponse<SoaSendLogSummary>>>(
    `/api/v1/soa/send-logs${qs}`,
    token,
  )
  return response.data
}

/**
 * Full SOA send log including the line-item snapshot — drives the View drawer.
 *
 * GET /api/v1/soa/send-logs/{id}
 */
export async function fetchSoaSendLogDetail(id: string): Promise<SoaSendLogDetail> {
  const token = getToken()
  const response = await authFetch<ApiResponse<SoaSendLogDetail>>(
    `/api/v1/soa/send-logs/${encodeURIComponent(id)}`,
    token,
  )
  return response.data
}

/**
 * Download the PDF that was actually emailed.
 *
 * Returns the bytes if the PDF is still in storage; throws ApiError(410) when
 * the PDF has been purged by the retention sweep — the UI should disable the
 * button with a tooltip explaining the policy.
 *
 * GET /api/v1/soa/send-logs/{id}/pdf
 */
export async function downloadSoaSendLogPdf(id: string, filename: string): Promise<void> {
  const token = getToken()
  const blob = await authFetchBlob(
    `/api/v1/soa/send-logs/${encodeURIComponent(id)}/pdf`,
    token,
  )
  triggerBlobDownload(blob, filename)
}
